import { type FormEvent, useEffect, useState } from 'react'
import { Flag, X } from 'lucide-react'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/Button'
import { cn } from '@/components/ui/cn'
import { copy } from '@/lib/copy'
import {
  buildPopupIssueReportMessage,
  type PopupIssueReportContext,
} from './buildPopupIssueReportMessage'

type PopupIssueReportProps = {
  context: PopupIssueReportContext
  open: boolean
  onOpenChange: (open: boolean) => void
  isMobileSheet?: boolean
}

export function PopupIssueReportButton({
  open,
  onOpenChange,
  isMobileSheet = false,
}: Pick<PopupIssueReportProps, 'open' | 'onOpenChange' | 'isMobileSheet'>) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        onOpenChange(!open)
      }}
      className={cn(
        'text-muted-foreground hover:text-foreground',
        open && 'text-foreground',
        isMobileSheet
          ? 'flex h-11 w-11 items-center justify-center rounded-md'
          : 'rounded-md p-1',
      )}
      aria-label={copy.word.reportIssue}
      aria-expanded={open}
      title={copy.word.reportIssue}
      data-testid="popup-report-button"
    >
      <Flag className="h-4 w-4" />
    </button>
  )
}

export function PopupIssueReportPanel({
  context,
  open,
  onOpenChange,
}: Omit<PopupIssueReportProps, 'isMobileSheet'>) {
  const [note, setNote] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle')

  useEffect(() => {
    if (!open) {
      setNote('')
      setStatus('idle')
    }
  }, [open])

  if (!open) return null

  const close = () => onOpenChange(false)

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const trimmed = note.trim()
    if (status === 'loading' || trimmed.length < 5) return

    setStatus('loading')
    try {
      const pagePath =
        context.pagePath
        ?? (typeof window !== 'undefined' ? window.location.pathname : undefined)
      await apiClient.post('/feedback', {
        category: 'DISLIKE',
        message: buildPopupIssueReportMessage(trimmed, context),
        pagePath,
      })
      setStatus('done')
      setNote('')
    } catch {
      setStatus('error')
    }
  }

  return (
    <div
      className="shrink-0 border-b border-border bg-muted/30 px-4 py-3"
      onClick={(e) => e.stopPropagation()}
      data-testid="popup-report-panel"
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <div>
          <p className="text-xs font-medium text-foreground">{copy.word.reportIssueTitle}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">{copy.word.reportIssueHint}</p>
        </div>
        <button
          type="button"
          onClick={close}
          className="rounded-md p-1 text-muted-foreground hover:text-foreground"
          aria-label={copy.word.reportIssueClose}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {status === 'done' ? (
        <p className="text-sm text-emerald-700 dark:text-emerald-400" data-testid="popup-report-done">
          {copy.word.reportIssueDone}
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-2">
          <textarea
            required
            minLength={5}
            maxLength={500}
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={copy.word.reportIssuePlaceholder}
            className="w-full resize-none rounded-md border border-border bg-background px-2.5 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={copy.word.reportIssuePlaceholder}
            data-testid="popup-report-input"
          />
          <Button
            type="submit"
            disabled={status === 'loading' || note.trim().length < 5}
            className="h-9 w-full"
            data-testid="popup-report-submit"
          >
            {status === 'loading' ? '…' : copy.word.reportIssueSubmit}
          </Button>
          {status === 'error' ? (
            <p className="text-xs text-destructive" data-testid="popup-report-error">
              {copy.word.reportIssueError}
            </p>
          ) : null}
        </form>
      )}
    </div>
  )
}
